import { useEffect, useRef, useState } from 'react';
import { DropDownList } from '../../../../../core/components/molecules/DropDownList';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { Button } from '../../../../../core/components/atoms/Button';
import TaskSwitch from './components/TaskSwitch';
import { ProgressBar } from '../../../../../core/components/atoms/ProgressBar';
import { IScheduleArrays, ScheduleDayEntry } from '../../../model/ScheduleHub';
import ScheduleList from './components/ScheduleList';
import { useGetHubWeekSchedule } from '../../../hooks/ScheduleHub/useGetHubWeekSchedule';
import isWithinInterval from 'date-fns/isWithinInterval';
import BrainLoadingWrapper from '../../../../../core/components/molecules/BrainLoading';
import { useCreateScheduleSSR } from '../../../hooks/WeekSchedule/useCreateScheduleSSR';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDayName } from '../../../../../core/utils/dataTools';
import ScheduleItemChangeModal, {
  IScheduleItemChangeModalHandle,
} from './components/ItemChangeModal';
import { ScheduleStartInfo } from '../../../utils/scheduleStart';
import { getSSRWeekPeriod } from '../../../utils/ssrPeriod';
import { format } from 'date-fns';
import { Tooltip } from 'react-tooltip';
import { useUserStore } from '../../../../Auth/stores/authStore';

interface IWeekActivitySchedule {
  cronogramaId?: string;
  weeks: IScheduleArrays[];
  startInfo: ScheduleStartInfo;
  onScheduleChange?: () => void;
}

const WeekActivitySchedule: React.FC<IWeekActivitySchedule> = ({ cronogramaId, weeks, onScheduleChange, startInfo }) => {
  const navigate = useNavigate();
  const { user } = useUserStore();

  const { mutate, isPending, isError, data } = useGetHubWeekSchedule();
  const { mutate: createSSR, isPending: isPendingSSR } = useCreateScheduleSSR();

  const [searchParams] = useSearchParams();
  const week = searchParams.get('week');

  const [selectedWeek, setSelectedWeek] = useState<IScheduleArrays | undefined>();
  const [showIncompleteTasks, setShowIncompleteTasks] = useState(false);
  const [scheduleDays, setScheduleDays] = useState<ScheduleDayEntry[]>([]);
  const itemChangeModalRef = useRef<IScheduleItemChangeModalHandle>(null);

  const [hasCompleteTasks, setHasCompleteTasks] = useState(false);

  useEffect(() => {
    if (scheduleDays.length > 0) {
      setHasCompleteTasks(scheduleDays.some((day) =>
        day.contents.some((content) => content.progress > 0)
      ));
    }
  }, [scheduleDays]);

  useEffect(() => {
    if (cronogramaId && weeks.length > 0) {
      if (!selectedWeek) {
        const actualWeek = weeks.find((week) =>
          isWithinInterval(new Date(), {
            start: new Date(week.start),
            end: new Date(week.end),
          })
        );

        if (actualWeek) {
          setSelectedWeek(actualWeek);
        } else {
          setSelectedWeek(weeks[0]);
        }
      }
    }
  }, [cronogramaId, weeks]);

  useEffect(() => {
    if (!week) return;

    handleSelectWeek(week);
  }, [week]);

  useEffect(() => {
    if (selectedWeek && cronogramaId) {
      mutate(
        {
          idCronograma: cronogramaId,
          startDate: new Date(selectedWeek.start),
          endDate: new Date(selectedWeek.end),
        },
        {
          onSuccess: (data) => {
            if (data.weekSchedule) {
              setScheduleDays(data.weekSchedule);
            }
          },
        }
      );
    }
  }, [selectedWeek]);

  const handleCardClick = (day: string, discipline: string, week: number) => {
    const dayName = getDayName(day);
    const url = `/plano-adaptativo/${cronogramaId}/dia?dia=${dayName}&materia=${discipline}&semana=${week}&data=${day}`;

    navigate(url, { replace: true });
  };

  const handleSelectWeek = (id: string) => {
    const week = weeks.find((week) => week.number.toString() === id);

    if (!week) return;

    navigate(window.location.pathname, {
      replace: true,
      state: { week: week?.number.toString() },
    });

    setSelectedWeek(week);
  };

  const handleHideDoneTasks = (status: boolean) => {
    setShowIncompleteTasks(status);

    if (scheduleDays.length > 0 && status) {
      setScheduleDays(
        scheduleDays
          .map((day) => ({
            ...day,
            contents: day.contents.filter((content) => content.progress < 100),
          }))
          .filter((day) => day.contents.length > 0)
      );
    } else {
      setScheduleDays(data?.weekSchedule || []);
    }
  };

  const handleCreateSSR = () => {
    if (!selectedWeek) return;

    //O título do simulado deve englobar a semana inteira (segunda a domingo),
    //e não apenas os dias em que houve conteúdo consumido
    createSSR(getSSRWeekPeriod(selectedWeek.start));
  };

  const handleFallbackDate = () => {
    if(startInfo && startInfo.fallbackDate) {
      return format(startInfo.fallbackDate, 'dd/MM/yyyy');
    }

    return format(new Date(), 'dd/MM/yyyy');
  };

  const showContent = () => {
    if (startInfo.status !== 'onTime' && !selectedWeek) {
      return (
        <div className='flex flex-col items-center'>
          <Typography className='text-center italic text-mono-color-darker'>
            Seu Cronograma será iniciado em{' '}
            {handleFallbackDate()}
          </Typography>
        </div>
      );
    }

    if (isPending || !data) {
      return <BrainLoadingWrapper size='sm' />;
    }

    if (isError || !data) {
      return null;
    }

    return (
      <>
        <div className='mb-4 flex flex-row items-center gap-3'>
          <Typography size='small' weight='bold'>
            Progresso
          </Typography>
          <ProgressBar
            progress={data?.progress || 0}
            backgroundColor='mono-color-lightest'
            className='w-full'
          />
        </div>
        <ScheduleList
          cronogramaId={cronogramaId}
          scheduleDays={scheduleDays}
          setScheduleDays={setScheduleDays}
          actualWeek={selectedWeek}
          onCardClick={handleCardClick}
          onScheduleChange={onScheduleChange}
        />
      </>
    );
  };

  return (
    <div data-tour="week-activity-schedule" className='w-full min-w-0 overflow-hidden rounded-lg bg-mono-color-white p-5'>
      <ScheduleItemChangeModal ref={itemChangeModalRef} />
      <div className='flex flex-col justify-between gap-md xl:flex-row xl:items-center xl:gap-0'>
        <div className='flex flex-row items-center gap-4'>
          <div data-tour="week-selector">
            <DropDownList
              noPadding
              size='small'
              items={weeks.map((week) => ({
                id: week.number.toString(),
                title: week.title,
                subtitle: week.subtitle,
              }))}
              onSelect={(item) => handleSelectWeek(item.id)}
              value={
                selectedWeek
                  ? {
                    id: selectedWeek.number.toString(),
                    title: selectedWeek.title,
                  }
                  : null
              }
            />
          </div>
          <Typography size='small'>{selectedWeek?.subtitle}</Typography>
        </div>
        <div className='flex flex-col justify-between gap-md lg:flex-row lg:items-center lg:gap-0'>
          <TaskSwitch
            showIncompleteTasks={showIncompleteTasks}
            setShowIncompleteTasks={handleHideDoneTasks}
          />
          <div className='flex flex-col items-start'>
            <Button
              data-tour="ssr-button"
              id='ssr-button'
              variant='text'
              size='small'
              LeftIcon='timer'
              iconType='material'
              iconSize='small'
              onClick={handleCreateSSR}
              loading={isPendingSSR}
              disabled={!hasCompleteTasks}
            >
              Simulado Semanal de Revisão (SSR)
            </Button>
            {!hasCompleteTasks && (
              <Tooltip
                opacity={1}
                offset={10}
                anchorSelect='#ssr-button'
                place='bottom'
                style={{
                  width: '220px',
                  background: user?.isDarkMode ? '#2E3847' : '#F0F3F7',
                  borderRadius: '8px',
                  padding: '10px',
                  zIndex: 10,
                }}
              >
                <Typography size='mini'>
                  O simulado será liberado após você concluir os estudos da semana.
                </Typography>

              </Tooltip>
            )}
          </div>
        </div>
      </div>
      <div className='my-3 w-full border-b-hairline border-b-mono-color-medium' />
      {showContent()}
    </div>
  );
};

export default WeekActivitySchedule;
